<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    /** 选项文案 */
    options: readonly string[]
    /** 当前选中项；`null` 表示无预设命中（例如手动改宽高后）。 */
    modelValue: string | null
    /** 无障碍分组名称（映射到 `aria-label`）。 */
    groupLabel: string
    /**
     * 为 `true` 时子项均分整行宽度（用于 Scene Settings 等三枚并排预设）。
     */
    equalWidth?: boolean
    /** 返回 `true` 时该项不可点选（仍可按 `modelValue` 显示为已选）。 */
    optionDisabled?: (opt: string) => boolean
    /** 悬停提示（例如禁用原因）；未返回则不加 `title`。 */
    optionTitle?: (opt: string) => string | undefined
  }>(),
  {
    equalWidth: false,
    optionDisabled: () => false,
    optionTitle: () => undefined,
  },
)

const emit = defineEmits<{
  (e: 'select', value: string): void
}>()

function isActive(opt: string): boolean {
  return props.modelValue === opt
}

function disabledFor(opt: string): boolean {
  return props.optionDisabled(opt)
}

function titleFor(opt: string): string | undefined {
  return props.optionTitle(opt)
}
</script>

<template>
  <div class="chips" :class="{ stretch: equalWidth }" :aria-label="groupLabel">
    <button
      v-for="opt in options"
      :key="opt"
      type="button"
      class="chip"
      :class="{ active: isActive(opt), 'chip--disabled': disabledFor(opt) }"
      :disabled="disabledFor(opt)"
      :title="titleFor(opt)"
      @click="emit('select', opt)"
    >
      {{ opt }}
    </button>
  </div>
</template>

<style scoped>
.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
}

.chips.stretch {
  flex-wrap: nowrap;
  width: 100%;
  gap: 0.5rem;
}

.chip {
  appearance: none;
  border: 1px solid var(--im-preset-border);
  background: var(--im-preset-bg);
  color: var(--im-text);
  border-radius: 10px;
  padding: 0.5rem 0.65rem;
  font-size: 0.8125rem;
  font-weight: 650;
  letter-spacing: 0.01em;
  cursor: pointer;
  text-align: center;
  transition:
    background 160ms ease,
    color 160ms ease,
    border-color 160ms ease,
    transform 120ms ease;
}

.chips.stretch .chip {
  flex: 1 1 0;
  min-width: 0;
}

.chip:hover {
  color: var(--im-text);
  border-color: var(--im-preset-border-hover);
  background: var(--im-preset-hover-bg);
}

.chip:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 2px;
}

.chip.active {
  color: var(--im-preset-active-fg);
  border-color: var(--im-preset-active-border);
  background: var(--im-preset-active-bg);
}

.chip--disabled,
.chip:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.chip--disabled:hover,
.chip:disabled:hover {
  border-color: var(--im-preset-border);
  background: var(--im-preset-bg);
  color: var(--im-text);
}

.chip:active {
  transform: translateY(1px);
}

.chip:disabled:active {
  transform: none;
}
</style>
